import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

const TOOLTIP_ID = "button-tooltip";

function tooltipTrigger(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element ? target.closest<HTMLElement>("[data-tooltip]") : null;
}

/** One overlay for button help, outside scrollable toolbars and without trigger wrappers. */
export function ButtonTooltips() {
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const current = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let hovered: HTMLElement | null = null;
    let focused: HTMLElement | null = null;
    let dismissed: HTMLElement | null = null;
    let touchFocus = false;
    let leaveTimer: number | undefined;
    let openFrame = 0;
    const cancelOpen = () => {
      cancelAnimationFrame(openFrame);
      openFrame = 0;
    };
    const cancelLeave = () => {
      clearTimeout(leaveTimer);
      leaveTimer = undefined;
    };
    const show = (next: HTMLElement | null) => {
      if (next === current.current) return;
      current.current = next;
      setTrigger(next);
    };
    const update = () => {
      if (dismissed !== hovered && dismissed !== focused) dismissed = null;
      const next = hovered ?? focused;
      show(next && next !== dismissed && next.isConnected ? next : null);
    };
    const openAfterLayout = () => {
      cancelOpen();
      openFrame = requestAnimationFrame(() => {
        openFrame = 0;
        update();
      });
    };
    const dismiss = () => {
      cancelOpen();
      cancelLeave();
      dismissed = current.current ?? hovered ?? focused;
      show(null);
    };
    const pointerMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || event.buttons !== 0) return;
      touchFocus = false;
      if (card.current?.contains(event.target as Node)) {
        cancelLeave();
        return;
      }
      const next = tooltipTrigger(event.target);
      if (next) {
        cancelLeave();
        if (next !== hovered) {
          hovered = next;
          show(null);
          openAfterLayout();
        } else if (!openFrame) update();
      } else if (hovered && leaveTimer === undefined) {
        cancelOpen();
        // Allow crossing the small gap between a trigger and the hoverable card.
        leaveTimer = window.setTimeout(() => {
          leaveTimer = undefined;
          hovered = null;
          update();
        }, 150);
      }
    };
    const pointerOut = (event: PointerEvent) => {
      if (event.pointerType === "mouse" && event.relatedTarget === null) {
        cancelLeave();
        cancelOpen();
        hovered = null;
        update();
      }
    };
    const pointerDown = (event: PointerEvent) => {
      touchFocus = event.pointerType !== "mouse";
      dismissed = tooltipTrigger(event.target) ?? current.current;
      cancelOpen();
      cancelLeave();
      hovered = null;
      show(null);
    };
    const focusIn = (event: FocusEvent) => {
      const next = tooltipTrigger(event.target);
      focused = !touchFocus && next?.matches(":focus-visible") ? next : null;
      hovered = null;
      cancelLeave();
      show(null);
      openAfterLayout();
    };
    const focusOut = (event: FocusEvent) => {
      const next = tooltipTrigger(event.relatedTarget);
      focused = !touchFocus && next?.matches(":focus-visible") ? next : null;
      openAfterLayout();
    };
    const keyDown = (event: KeyboardEvent) => {
      touchFocus = false;
      if (event.key === "Escape") dismiss();
    };
    const scroll = (event: Event) => {
      // Focus/hover can first scroll a narrow toolbar into view. Let that pending
      // opening use the final geometry; only dismiss an already-visible card.
      if (current.current && !(event.target instanceof Node && card.current?.contains(event.target))) dismiss();
    };
    document.addEventListener("pointerover", pointerMove, true);
    // Pointer movement also reaches disabled buttons, which cannot receive focus or clicks.
    document.addEventListener("pointermove", pointerMove, true);
    document.addEventListener("pointerout", pointerOut, true);
    document.addEventListener("pointerdown", pointerDown, true);
    document.addEventListener("click", dismiss, true);
    document.addEventListener("focusin", focusIn, true);
    document.addEventListener("focusout", focusOut, true);
    document.addEventListener("keydown", keyDown, true);
    document.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", dismiss);
    window.visualViewport?.addEventListener("resize", dismiss);
    window.visualViewport?.addEventListener("scroll", scroll);
    return () => {
      cancelOpen();
      cancelLeave();
      document.removeEventListener("pointerover", pointerMove, true);
      document.removeEventListener("pointermove", pointerMove, true);
      document.removeEventListener("pointerout", pointerOut, true);
      document.removeEventListener("pointerdown", pointerDown, true);
      document.removeEventListener("click", dismiss, true);
      document.removeEventListener("focusin", focusIn, true);
      document.removeEventListener("focusout", focusOut, true);
      document.removeEventListener("keydown", keyDown, true);
      document.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("blur", dismiss);
      window.visualViewport?.removeEventListener("resize", dismiss);
      window.visualViewport?.removeEventListener("scroll", scroll);
    };
  }, []);

  useLayoutEffect(() => {
    const element = card.current;
    if (!trigger || !element) return;
    const descriptions = trigger.getAttribute("aria-describedby")?.split(/\s+/).filter(Boolean) ?? [];
    const addedDescription = !descriptions.includes(TOOLTIP_ID);
    if (addedDescription) trigger.setAttribute("aria-describedby", [...descriptions, TOOLTIP_ID].join(" "));
    const position = () => {
      const viewport = window.visualViewport;
      const left = (viewport?.offsetLeft ?? 0) + 8;
      const top = (viewport?.offsetTop ?? 0) + 8;
      const width = viewport?.width ?? window.innerWidth;
      const height = viewport?.height ?? window.innerHeight;
      const right = left + width - 16;
      const bottom = top + height - 16;
      element.style.maxWidth = `${Math.max(1, width - 16)}px`;
      element.style.maxHeight = `${Math.max(1, height - 16)}px`;
      const anchor = trigger.getBoundingClientRect();
      const bounds = element.getBoundingClientRect();
      const below = anchor.bottom + 8;
      const above = anchor.top - bounds.height - 8;
      const preferredTop = below + bounds.height <= bottom || above < top ? below : above;
      element.style.left = `${Math.max(left, Math.min(anchor.left + (anchor.width - bounds.width) / 2, right - bounds.width))}px`;
      element.style.top = `${Math.max(top, Math.min(preferredTop, bottom - bounds.height))}px`;
    };
    position();
    const resize = new ResizeObserver(position);
    resize.observe(trigger);
    resize.observe(element);
    const removed = new MutationObserver(() => {
      if (!trigger.isConnected) {
        current.current = null;
        setTrigger(null);
      } else {
        position();
      }
    });
    removed.observe(document.body, { childList: true, subtree: true });
    return () => {
      resize.disconnect();
      removed.disconnect();
      if (addedDescription) {
        const remaining = trigger.getAttribute("aria-describedby")?.split(/\s+/).filter((id) => id && id !== TOOLTIP_ID) ?? [];
        if (remaining.length) trigger.setAttribute("aria-describedby", remaining.join(" "));
        else trigger.removeAttribute("aria-describedby");
      }
    };
  }, [trigger]);

  if (!trigger) return null;
  const keys = trigger.dataset.tooltipKeys?.split("|").filter(Boolean) ?? [];
  return (
    <div class="button-tooltip" id={TOOLTIP_ID} role="tooltip" ref={card}>
      <strong>{trigger.dataset.tooltip}</strong>
      {trigger.dataset.tooltipDescription && <p>{trigger.dataset.tooltipDescription}</p>}
      {keys.length > 0 && <div class="button-tooltip-keys">{keys.map((key) => <kbd key={key}>{key}</kbd>)}</div>}
    </div>
  );
}
